import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { useT } from '../context/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const t = useT();
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError(t({ fr: 'Merci de remplir nom, e-mail et message.', en: 'Please fill in name, email and message.' }));
      return;
    }
    setStatus('sending');
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || 'Send failed');
      }
      setStatus('sent');
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      setStatus('error');
      setError(err.message || t({ fr: 'Envoi impossible.', en: 'Send failed.' }));
    }
  };

  return (
    <div className="bg-[#FAF9F7]" data-testid="contact-page">
      <section className="py-20 bg-gradient-to-br from-[#2B3E5F] via-[#3d5372] to-[#2B3E5F]">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl lg:text-6xl font-serif text-[#FAF9F7] mb-6" data-testid="contact-title">
            {t({ fr: 'Nous contacter', en: 'Contact Us' })}
          </h1>
          <p className="text-xl text-[#D4AF37] max-w-3xl mx-auto">
            {t({
              fr: 'Des questions ? Nous sommes là pour vous accompagner dans votre parcours en français.',
              en: "Have questions? We're here to help you on your French learning journey",
            })}
          </p>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            <div className="bg-[#FAF9F7] border-2 border-[#D4C4B0] rounded-xl p-8">
              <h2 className="text-3xl font-serif text-[#2C2C2C] mb-6" data-testid="contact-form-title">
                {t({ fr: 'Envoyez-nous un message', en: 'Send us a Message' })}
              </h2>
              {status === 'sent' ? (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 px-5 py-6 text-center" data-testid="contact-success">
                  <CheckCircle className="w-8 h-8 text-emerald-600 mx-auto mb-2" />
                  <p className="text-emerald-800 font-semibold">
                    {t({ fr: 'Message envoyé — merci !', en: 'Message sent — thank you!' })}
                  </p>
                  <p className="text-sm text-emerald-700 mt-1">
                    {t({
                      fr: 'Un accusé de réception vient d\u2019arriver dans ta boîte mail. Nous te répondons sous un jour ouvré.',
                      en: 'A confirmation just landed in your inbox. We\u2019ll reply within one working day.',
                    })}
                  </p>
                </div>
              ) : (
                <form className="space-y-6" data-testid="contact-form" onSubmit={submit}>
                  <div>
                    <label className="block text-[#2C2C2C] font-medium mb-2">{t({ fr: 'Nom complet', en: 'Full Name' })}</label>
                    <input type="text" className="w-full px-4 py-3 border-2 border-[#D4C4B0] rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors" placeholder={t({ fr: 'Votre nom', en: 'Your name' })} value={form.name} onChange={update('name')} data-testid="contact-name" />
                  </div>
                  <div>
                    <label className="block text-[#2C2C2C] font-medium mb-2">{t({ fr: 'Adresse e-mail', en: 'Email Address' })}</label>
                    <input type="email" className="w-full px-4 py-3 border-2 border-[#D4C4B0] rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors" placeholder="your.email@example.com" value={form.email} onChange={update('email')} data-testid="contact-email" />
                  </div>
                  <div>
                    <label className="block text-[#2C2C2C] font-medium mb-2">{t({ fr: 'Sujet', en: 'Subject' })}</label>
                    <input type="text" className="w-full px-4 py-3 border-2 border-[#D4C4B0] rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors" placeholder={t({ fr: 'Comment pouvons-nous vous aider ?', en: 'How can we help?' })} value={form.subject} onChange={update('subject')} data-testid="contact-subject" />
                  </div>
                  <div>
                    <label className="block text-[#2C2C2C] font-medium mb-2">Message</label>
                    <textarea rows="6" className="w-full px-4 py-3 border-2 border-[#D4C4B0] rounded-lg focus:border-[#D4AF37] focus:outline-none transition-colors resize-none" placeholder={t({ fr: 'Parlez-nous davantage de votre demande...', en: 'Tell us more about your inquiry...' })} value={form.message} onChange={update('message')} data-testid="contact-message"></textarea>
                  </div>
                  {error && <p className="text-red-600 text-sm" data-testid="contact-error">{error}</p>}
                  <button type="submit" disabled={status === 'sending'} className="w-full bg-[#D4AF37] text-[#2B3E5F] px-8 py-4 rounded-lg font-bold hover:bg-[#C4B5A0] transition-all shadow-lg flex items-center justify-center gap-2 disabled:opacity-60" data-testid="contact-submit">
                    {status === 'sending' ? t({ fr: 'Envoi en cours...', en: 'Sending...' }) : t({ fr: 'Envoyer le message', en: 'Send Message' })}
                    <Send className="w-5 h-5" />
                  </button>
                </form>
              )}
            </div>

            <div>
              <h2 className="text-3xl font-serif text-[#2C2C2C] mb-8">
                {t({ fr: 'Restons en contact', en: 'Get in Touch' })}
              </h2>
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4" data-testid="contact-email-block">
                  <div className="w-12 h-12 bg-[#C4B5A0] rounded-full flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6 text-[#2B3E5F]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-serif text-[#2C2C2C] mb-1">
                      {t({ fr: 'E-mail', en: 'Email' })}
                    </h3>
                    <a href="mailto:info@seloraacademy.com" className="text-[#6B4E37] hover:text-[#D4AF37] transition-colors" data-testid="contact-email-link">
                      info@seloraacademy.com
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-[#F5F3F0] rounded-xl p-8">
                <h3 className="text-xl font-serif text-[#2C2C2C] mb-4">
                  {t({ fr: 'Actions rapides', en: 'Quick Actions' })}
                </h3>
                <div className="space-y-3">
                  <Link to="/teachers-portal" className="block text-[#6B4E37] hover:text-[#D4AF37] transition-colors" data-testid="quick-action-book-demo">
                    → {t({ fr: 'Réserver une session de démo', en: 'Book a Demo Session' })}
                  </Link>
                  <Link to="/teachers-portal" className="block text-[#6B4E37] hover:text-[#D4AF37] transition-colors" data-testid="quick-action-teacher-partnership">
                    → {t({ fr: 'Partenariat avec les enseignants', en: 'Teacher Partnership Inquiry' })}
                  </Link>
                  <a href="mailto:info@seloraacademy.com?subject=Corporate%20Training%20Inquiry" className="block text-[#6B4E37] hover:text-[#D4AF37] transition-colors" data-testid="quick-action-corporate-training">
                    → {t({ fr: 'Formation en entreprise', en: 'Corporate Training' })}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
